import { useState } from "react";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import { getSeasonsUrl } from "../../config/api";
import { apiPost } from "../../utils/apiClient";

interface SeasonDetailsStepProps {
  onNext: (season: any) => void;
  leagueId: number;
  token: string;
  leagueAbbr: string;
}

function SeasonDetailsStep({
  onNext,
  leagueId,
  token,
  leagueAbbr,
}: SeasonDetailsStepProps) {
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");
    setIsLoading(true);

    const formData = new FormData(event.currentTarget);
    const name = formData.get("name") as string;
    const start = formData.get("start") as string;

    // Validation
    if (!name || name.length < 3) {
      setErrorMessage("Season name must be at least 3 characters");
      setIsLoading(false);
      return;
    }

    try {
      const result = await apiPost(
        getSeasonsUrl(leagueId),
        {
          season: {
            name,
            ...(start ? { start } : {}),
          },
        },
        { token, leagueAbbr },
      );

      onNext(result);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "An error occurred";
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h4 className="mb-3">Season Details</h4>
      <p className="text-muted mb-4">
        Create a season to organize games and track standings.
      </p>

      {errorMessage && <Alert variant="warning">{errorMessage}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3" controlId="formSeasonName">
          <Form.Label>Season Name</Form.Label>
          <Form.Control
            type="text"
            name="name"
            placeholder="Enter season name"
            required
            disabled={isLoading}
          />
          <Form.Text className="text-muted">
            e.g., "2024-2025" or "Fall 2024"
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3" controlId="formSeasonStart">
          <Form.Label>Start Date (Optional)</Form.Label>
          <Form.Control type="date" name="start" disabled={isLoading} />
        </Form.Group>

        <div className="d-grid">
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading && (
              <Spinner animation="border" size="sm" className="me-2" />
            )}
            Next: Add Teams
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default SeasonDetailsStep;
